import { supabase, run } from './db';
import { cached } from './local/cache';
import { queueable } from './local/queued';

/**
 * Animal doador de cada embrião. Os relatórios de um serviço que herdou a OPU
 * casam por aqui as doadoras da coleta com o que foi transferido nele.
 */
export const fetchDoadorasDosEmbrioes = (ids = []) =>
  ids.length === 0 ? Promise.resolve([]) : run(supabase
    .from('embryos')
    .select('id, donor_id')
    .in('id', ids));

/** Embriões de um serviço, na ordem de numeração. */
export const fetchEmbryosByService = (serviceId, columns = '*') =>
  cached(`embryos:${serviceId}:${columns}`, () => run(supabase
    .from('embryos')
    .select(columns)
    .eq('service_id', serviceId)
    .order('service_number')));

/**
 * Embriões que a TE pode listar: disponíveis e já transferidos.
 *
 * Inclui `transferred` de propósito — a aba precisa mostrar o que já saiu para
 * não parecer que sumiu. Descartados e congelados sem DT ficam de fora.
 *
 * Vem da view `embryos_com_uso`, que traz junto o serviço onde cada um foi
 * usado. Sem isso, uma FIV ligada a duas TEs mostrava todos como disponíveis
 * nas duas, e o mesmo embrião podia ser transferido duas vezes.
 */
/**
 * De quais serviços a TE pode puxar embrião.
 *
 * O próprio, mais o de ORIGEM quando o serviço herda a FIV de outro. Sem isso
 * um serviço que só faz a transferência — embrião produzido no serviço vizinho
 * — abria a tela de TE vazia, sem nada explicando por quê: o vínculo existia,
 * cascateava as datas, e não trazia os embriões junto.
 *
 * Herdar a OPU não basta: quem cedeu só a coleta não produziu embrião nenhum
 * para este serviço. É a FIV que gera o que se transfere.
 */
const servicosComEmbrioes = (service) => {
  const ids = [service?.id];
  if (service?.source_stage === 'fiv' && service?.source_service_id) {
    ids.push(service.source_service_id);
  }
  return ids.filter(Boolean);
};

// A chave do cache carrega os dois ids: com só o do serviço, ligar ou trocar a
// origem devolveria a lista antiga guardada.
const chaveDeCache = (prefixo, ids) => `${prefixo}:${ids.join('+')}`;

export const fetchEmbryosForTe = (service, laboratoryId) => {
  const ids = servicosComEmbrioes(service);
  return cached(chaveDeCache('embryos-te', ids), async () => {
    const [doLote, doEstoque] = await Promise.all([
      run(supabase
        .from('embryos_com_uso')
        .select('*, donor:donor_id(identifier, breed, rgd)')
        .in('service_id', ids)
        .eq('laboratory_id', laboratoryId)
        // 'frozen' ENTRA. Sem ele, todo embrião vitrificado ficava invisível na
        // TE — e o botijão existe justamente para ser transferido depois. No
        // serviço #2026-0036, isso escondia 8 dos 49 embriões, incluindo os 3 da
        // doadora CHINESA, que não tinha nenhum fresco: a doadora sumia inteira da
        // tela sem nada explicando por quê.
        //
        // 'discard' segue de fora: descartado não volta.
        .in('destination', ['fresh', 'frozen', 'transferred'])
        .order('service_number')),
      descongeladosDaTe(service, laboratoryId, ids),
    ]);
    return [...(doLote || []), ...doEstoque];
  });
};

/**
 * Marca o embrião que veio do estoque, e não do lote desta TE.
 *
 * O número dele é de outro serviço (ou da etiqueta de quem produziu): a TE
 * não o edita, não o confere contra os daqui e não o conta no saldo do lote.
 */
const doEstoque = (e) => ({ ...e, _doEstoque: true });

/**
 * Os descongelados que esta TE já usou.
 *
 * Embrião do estoque é de outro serviço, e a consulta do lote não o traz.
 * Sem isto ele sumia da TE depois de salvar, e a transferência dele virava
 * linha "sintética", sem doadora.
 */
const descongeladosDaTe = async (service, laboratoryId, ids) => {
  const sessoes = await run(supabase.from('te_sessions').select('id')
    .eq('service_id', service.id)) || [];
  if (sessoes.length === 0) return [];
  const transferencias = await run(supabase.from('te_transfers').select('embryo_id')
    .in('te_session_id', sessoes.map(s => s.id))
    .not('embryo_id', 'is', null)) || [];
  const embrioes = [...new Set(transferencias.map(t => t.embryo_id))];
  if (embrioes.length === 0) return [];
  const [linhas, donos] = await Promise.all([
    run(supabase.from('embryos_com_uso')
      .select('*, donor:donor_id(identifier, breed, rgd)')
      .in('id', embrioes)
      .eq('laboratory_id', laboratoryId)),
    // De quem era e de que serviço: a view não traz, e a linha precisa dizer
    // que o embrião é genética de outro cliente.
    run(supabase.from('embryos')
      .select('id, client_id, client:client_id(name), service:service_id(numero, client_id, client_name)')
      .in('id', embrioes)
      .eq('laboratory_id', laboratoryId)),
  ]);
  const donoPorId = Object.fromEntries((donos || []).map(d => [d.id, {
    servico_numero: d.service?.numero ?? null,
    client_id: d.service?.client_id ?? d.client_id ?? null,
    client_name: d.service?.client_name ?? d.client?.name ?? null,
  }]));
  return (linhas || []).filter(e => !ids.includes(e.service_id))
    .map(e => doEstoque({ ...e, ...donoPorId[e.id] }));
};

/**
 * O estoque que a TE pode descongelar: o do laboratório inteiro.
 *
 * Primeiro era só o do cliente do serviço, mas genética se vende: na TE da
 * Fazenda Santa Rita entraram VT dela e do Bruno Brito. O embrião segue sendo
 * do dono até a transferência ser aprovada; aí sai do botijão dele e conta
 * na TE de quem recebeu, que é quem paga a entrada.
 *
 * Congelado de qualquer serviço ou sem serviço (chegou de fora). O lote desta
 * TE fica de fora: esse já está na lista. Os do próprio cliente vêm antes.
 *
 * Vem no formato da TE, para entrar na mesma tabela das outras linhas.
 */
export const estoqueParaTe = async (service, laboratoryId) => {
  if (!laboratoryId) return [];
  const ids = servicosComEmbrioes(service);
  const linhas = await run(supabase.from('vw_estoque_congelado').select('*')
    .eq('laboratory_id', laboratoryId)
    .order('client_name')
    .order('haste', { nullsFirst: false })
    .order('doadora')) || [];
  const doCliente = (l) => (service?.client_id && l.client_id === service.client_id ? 0 : 1);
  return linhas
    .filter(l => !ids.includes(l.service_id))
    .sort((a, b) => doCliente(a) - doCliente(b))
    .map(l => doEstoque({
      id: l.embryo_id,
      embryo_id: l.embryo_id,
      service_id: l.service_id,
      service_number: l.numero,
      donor_identifier: l.doadora,
      bull_name: l.touro,
      stage: l.stage,
      destination: 'frozen',
      freeze_method: l.metodo,
      freeze_date: l.freeze_date,
      botijao: l.botijao, caneca: l.caneca, rack: l.rack, altura: l.altura,
      haste: l.haste,
      servico_numero: l.servico_numero,
      de_fora: l.de_fora,
      client_id: l.client_id,
      client_name: l.client_name,
    }));
};

/**
 * Versão do mobile: só os que ainda estão livres para transferir.
 *
 * Inclui congelado pela mesma razão do desktop. `recipient_id is null` é o que
 * garante "livre" — e ele vale para os dois destinos.
 */
export const fetchFreshEmbryosForTe = (service, laboratoryId) => {
  const ids = servicosComEmbrioes(service);
  return cached(chaveDeCache('embryos-te-fresh', ids), () => run(supabase
    .from('embryos_com_uso')
    // `rgd` entra junto porque a ficha da TE imprime os dois: na fazenda, dois
    // animais de mesmo nome só se separam pelo registro.
    .select('*, donor:donor_id(identifier, breed, rgd)')
    .in('service_id', ids)
    .eq('laboratory_id', laboratoryId)
    .in('destination', ['fresh', 'frozen'])
    .is('recipient_id', null)
    .order('service_number')));
};

export const insertEmbryos = (rows) =>
  run(supabase.from('embryos').insert(rows));

/**
 * Embriões manuais lançados na aba de TE.
 *
 * Antes isto inseria e ESPERAVA o servidor devolver os ids, que eram então
 * ligados às transferências. Offline não havia resposta, os ids provisórios
 * ficavam e a transferência apontava para embrião inexistente.
 *
 * Agora o id vem do aparelho: quem chama já sabe o id antes de gravar, e o
 * vínculo se resolve na hora, com ou sem conexão.
 */
export const insertManualEmbryos = queueable('embryo.manual.insert',
  (rows) => run(supabase.from('embryos').insert(rows)),
  {
    patchOf: (rows) => (rows?.[0] ? {
      chave: `embryos-te:${rows[0].service_id}`,
      tipo: 'insere',
      linha: rows[0],
    } : null),
  });

export const newEmbryoId = () => crypto.randomUUID();

/**
 * Quantos embriões da FIV deste serviço já saíram da FIV: transferidos, com
 * receptora, com saída lançada ou citados numa transferência da TE.
 *
 * É o que BLOQUEIA o "Substituir pela OPU". A FIV substituída troca as linhas
 * de doadora; embrião sem uso é refeito pelas contagens, mas o usado ficaria
 * sem doadora de origem (a FK é ON DELETE SET NULL) — o estrago que só aparece
 * semanas depois, no relatório por doadora.
 *
 * Sem cache de propósito: a pergunta é "agora", logo antes de descartar.
 */
export const embrioesUsadosDaFIV = async (serviceId) => {
  const embrioes = await run(supabase
    .from('embryos')
    .select('id, destination, recipient_id, saida_em')
    .eq('service_id', serviceId)
    .not('fiv_donor_id', 'is', null)) || [];
  const usados = new Set(embrioes
    .filter(e => e.destination === 'transferred' || e.recipient_id || e.saida_em)
    .map(e => e.id));
  const livres = embrioes.filter(e => !usados.has(e.id)).map(e => e.id);
  if (livres.length > 0) {
    const citados = await run(supabase
      .from('te_transfers').select('embryo_id').in('embryo_id', livres)) || [];
    citados.forEach(t => usados.add(t.embryo_id));
  }
  return usados.size;
};

/**
 * Apaga os embriões de uma doadora, preservando os já transferidos.
 *
 * O `neq('destination', 'transferred')` não é otimização: embrião transferido
 * pode não ter recipient_id vinculado (aprovação sem receptora escolhida), e
 * sem esse filtro o regravar da FIV apagaria transferência já realizada.
 */
export const deleteUntransferredEmbryosOfDonor = (donorId, laboratoryId) =>
  run(supabase.from('embryos').delete()
    .eq('fiv_donor_id', donorId)
    .eq('laboratory_id', laboratoryId)
    .neq('destination', 'transferred'));


/**
 * O embrião está livre para ESTE serviço?
 *
 * Usado noutro serviço não entra: transferido é transferido, e oferecer de
 * novo abriria a porta para o mesmo embrião sair duas vezes. Usado NESTE
 * continua disponível para edição — são as transferências da própria sessão.
 */
export const embriaoDisponivelPara = (embriao, serviceId) =>
  !embriao?.usado_service_id || embriao.usado_service_id === serviceId;

/**
 * Os embriões que a TE DESTE serviço de fato movimentou.
 *
 * A tela lista o lote inteiro da FIV de origem, porque é dali que se escolhe.
 * O papel não: o PDF da TE de um serviço que herda a FIV saía com uma linha
 * por embrião do lote — doadora, touro e estágio do que outros serviços,
 * muitas vezes de outros clientes, transferiram. Entra só o que tem
 * transferência lançada aqui, aprovada ou reprovada.
 */
export const embrioesDaTE = (embrioes = [], transferencias = {}, serviceId) =>
  embrioes.filter(e => {
    const t = transferencias[e.id];
    return embriaoDisponivelPara(e, serviceId)
      && Boolean(t && (t.status || t.recipient?.identifier));
  });

/**
 * Saldo da FIV: produzido e ainda não usado.
 *
 * Descartado sai da conta porque deixou de existir como embrião; transferido
 * sai porque já foi. O que resta é o que o laboratório ainda tem em mãos —
 * inclusive congelado, que continua sendo saldo.
 */
export const saldoDeEmbrioes = (embrioes = []) => {
  const produzidos = embrioes.length;
  const descartados = embrioes.filter(e => e.destination === 'discard').length;
  const transferidos = embrioes.filter(e => e.usado_service_id).length;

  return {
    produzidos,
    descartados,
    transferidos,
    saldo: produzidos - descartados - transferidos,
  };
};


/**
 * Embriões de uma doadora, para numerar.
 *
 * TODOS os que ela produziu — não só os transferidos. Numerar é identificar o
 * que existe na bancada; travar na transferência impedia numerar antes de
 * transferir, que é justamente quando o número é preciso.
 *
 * Vem da view para trazer junto em que serviço cada um foi usado: a tela mostra
 * isso ao lado, sem impedir a edição.
 */
export const fetchEmbryosDaDoadora = (serviceId) =>
  run(supabase
    .from('embryos_com_uso')
    .select('*')
    .eq('service_id', serviceId)
    .order('service_number'));

/**
 * Numeração e fase por embrião.
 *
 * Vai por RPC e não por update solto porque as duas coisas precisam andar
 * juntas: mudar a fase de um embrião desacerta as contagens do doador, que são
 * o que a FIV usa para decidir quantos existem de cada estágio. Gravando só o
 * embrião, a próxima gravação da FIV desfaria a mudança.
 *
 * Não é enfileirável: depende de ler o estado atual dos embriões para
 * recalcular as contagens, e reenviar às cegas poderia gravar por cima de uma
 * numeração feita nesse meio-tempo.
 */
export const salvarNumeracao = (serviceId, embrioes) =>
  run(supabase.rpc('salva_numeracao_embrioes', {
    p_service_id: serviceId,
    p_embrioes: embrioes.map(e => ({
      id: e.id,
      service_number: e.service_number,
      stage: e.stage,
    })),
  }));

/** Número repetido some com a palheta: é por ele que a equipe a encontra. */
export const numerosRepetidos = (embrioes = []) => {
  const vistos = new Map();
  embrioes.forEach(e => {
    const n = e.service_number;
    if (n === null || n === undefined || n === '') return;
    vistos.set(String(n), (vistos.get(String(n)) || 0) + 1);
  });
  return [...vistos.entries()].filter(([, q]) => q > 1).map(([n]) => n);
};

/**
 * Ordem padrão da numeração: da fase mais avançada para a menos.
 *
 * Na tela os estágios aparecem BI → BE porque é a ordem do desenvolvimento.
 * Numerar segue o caminho inverso: quem está na bancada procura primeiro o
 * blastocisto eclodido, e espera achá-lo no começo da numeração.
 *
 * Espelha o laço de `reconcilia_embrioes_do_doador` no banco. Os dois têm que
 * concordar — um cria o número, o outro o refaz — e por isso a ordem está
 * escrita por extenso nos dois lugares, com um apontando para o outro.
 */
const ORDEM_DE_NUMERACAO = ['BX-ECLO', 'BN', 'BX', 'BL', 'BI'];

/**
 * Numerar é ato sobre embrião que ainda vai para a transferência.
 *
 * O congelado não entra: a palheta já está no botijão, fechada e etiquetada, e
 * o número dela não se decide mais numa tela. O descarte também não — não há
 * o que achar depois.
 *
 * Isto não era necessário enquanto o congelado saía do bolo dos estágios, e
 * passou a ser quando ele virou contagem própria: os embriões congelados
 * ganharam linha e apareceram na lista pedindo número.
 *
 * O BANCO DIZ O MESMO, e é lá que o número nasce: `embriao_vai_a_campo` é o
 * par exato desta função, e `reconcilia_embrioes_do_doador` só numera o grupo
 * que ela aprova. Filtrar só aqui escondia o congelado da TELA e deixava ele
 * gastando número na criação — a numeração do campo saía picotada.
 */
export const numeravel = (e) =>
  // Embrião nenhum não é numerável. Sem esta linha, `undefined` cairia no
  // `?? 'fresh'` de baixo e passaria por embrião fresco.
  !!e && !e.freeze_method && ['fresh', 'transferred'].includes(e.destination ?? 'fresh');

/** Embrião sem fase vai para o fim: não há onde encaixá-lo na sequência. */
const posicaoDaFase = (stage) => {
  const i = ORDEM_DE_NUMERACAO.indexOf(stage);
  return i === -1 ? ORDEM_DE_NUMERACAO.length : i;
};

/**
 * Reordena para numerar, sem numerar.
 *
 * Dentro do mesmo estágio a ordem anterior é preservada, para o clique não
 * embaralhar o que a equipe já tinha arrumado à mão.
 */
export const ordenaParaNumerar = (embrioes = []) =>
  embrioes
    .map((e, i) => ({ e, i }))
    .sort((a, b) => posicaoDaFase(a.e.stage) - posicaoDaFase(b.e.stage) || a.i - b.i)
    .map(({ e }) => e);

/** Número do embrião como inteiro, ou null quando ainda não tem. */
const numeroDoEmbriao = (e) => {
  const bruto = e?.service_number;
  if (bruto === null || bruto === undefined || bruto === '') return null;
  const n = Number(bruto);
  return Number.isFinite(n) ? n : null;
};

/**
 * Renumera os embriões de UMA doadora: bloco consecutivo, BE → BI.
 *
 * A numeração é do serviço, não da doadora: a doadora seguinte começa onde a
 * anterior parou, e dois embriões do mesmo serviço nunca dividem um número.
 * Dentro do bloco a ordem é a da fase mais avançada para a menos, igual à do
 * banco ao criar.
 *
 * O bloco entra logo depois de quem vem antes dela — e "antes" é quem tem
 * número menor, porque o número É a sequência do serviço. Buraco deixado por
 * embrião apagado se fecha: a doadora sai com uma faixa corrida.
 *
 * As outras doadoras não são tocadas. Renumerar uma não pode mexer no número
 * que já foi escrito na palheta de outra.
 */
export const renumeraDaDoadora = (daDoadora = [], doServico = []) => {
  if (daDoadora.length === 0) return [];

  const idsDela = new Set(daDoadora.map(e => e.id));
  const deOutras = doServico
    .filter(e => !idsDela.has(e.id))
    .map(numeroDoEmbriao)
    .filter(n => n !== null);
  const dela = daDoadora.map(numeroDoEmbriao).filter(n => n !== null);

  // Sem número ainda, a doadora vai para o fim da fila do serviço.
  const menorDela = dela.length > 0 ? Math.min(...dela) : Infinity;
  const anteriores = deOutras.filter(n => n < menorDela);
  let inicio = (anteriores.length > 0 ? Math.max(...anteriores) : 0) + 1;

  // Outra doadora ocupando o meio do intervalo: o bloco não cabe ali. Vai para
  // o fim, que é o único lugar garantidamente livre — repetir número seria
  // pior que mudar de faixa, porque some com a palheta das duas.
  const ocupados = new Set(deOutras);
  const cabe = (de) => daDoadora.every((_, i) => !ocupados.has(de + i));
  if (!cabe(inicio)) inicio = Math.max(0, ...deOutras, ...dela) + 1;

  return ordenaParaNumerar(daDoadora)
    .map((e, i) => ({ ...e, service_number: inicio + i }));
};
